import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FetchUsers } from "../../../../api/General/account";
import { getCompany } from "../../../../api/General/Company";
import { CompanyView, Roles, UserView } from "../../../../models/general";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from "../../../../components/ui/Table";
import Button from "../../../../components/ui/Button";
import Input from "../../../../components/ui/Input";
import Select from "../../../../components/ui/Select";
import { StatusBadge } from "../../../../components/ui/StatusBadge";
import { Spinner } from "../../../../components/ui/Spinner";

export default function CompanyUsersPage() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const role = useSelector((s: any) => s.user.role);

  const companyId = Number(params.id);
  const isOperator = role === Roles.Operator || role === "Operator";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [company, setCompany] = useState<CompanyView | null>(null);
  const [users, setUsers] = useState<UserView[]>([]);

  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("");

  const fetchAll = useCallback(async () => {
    if (!companyId || Number.isNaN(companyId)) return;

    setLoading(true);
    setError("");
    try {
      const [companyRes, usersRes] = await Promise.all([
        getCompany(companyId),
        FetchUsers("Company", companyId, showArchived),
      ]);

      setCompany(companyRes.payload);
      setUsers(usersRes.payload || []);
    } catch (e: any) {
      const msg = e?.errors?.[0]?.description || e?.message || t("Failed to load users");
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [companyId, showArchived, t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const availableRoles = useMemo(() => {
    const set = new Set<string>();
    for (const u of users) {
      if (u?.role) set.add(u.role);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const s = search.trim().toLowerCase();
    return users
      .filter((u) => (roleFilter ? u.role === roleFilter : true))
      .filter((u) => {
        if (!s) return true;
        return (
          (u.fullName || "").toLowerCase().includes(s) ||
          (u.email || "").toLowerCase().includes(s) ||
          (u.employeeId || "").toLowerCase().includes(s) ||
          (u.department?.name || "").toLowerCase().includes(s) ||
          (u.branch?.name || "").toLowerCase().includes(s)
        );
      });
  }, [users, search, roleFilter]);

  if (!isOperator) {
    return <Navigate to="/settings/general-details" />;
  }

  if (!companyId || Number.isNaN(companyId)) {
    return (
      <div className="p-4">
        <div role="alert" className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {t("Invalid company id")}
        </div>
      </div>
    );
  }

  const roleOptions = [
    { value: "", label: t("All") },
    ...availableRoles.map((r) => ({ value: r, label: r })),
  ];

  const archivedOptions = [
    { value: "no", label: t("Hide archived") },
    { value: "yes", label: t("Include archived") },
  ];

  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t("Company Users")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("Company")}: {company?.name ?? "..."} (ID: {companyId})
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate("/settings/companies")}>{t("Back to Companies")}</Button>
            <Button variant="outline" onClick={() => navigate(`/settings/companies/${companyId}`)}>{t("Company Details")}</Button>
            <Button onClick={() => navigate(`/settings/companies/${companyId}/batch-add-users`)}>{t("Batch Add Users")}</Button>
            <Button variant="outline" onClick={() => navigate(`/settings/companies/${companyId}/welcome-emails`)}>{t("Welcome Emails")}</Button>
          </div>
          <Button variant="outline" onClick={fetchAll} disabled={loading}>
            {t("Refresh")}
          </Button>
        </div>

        <hr className="border-border" />

        {error && (
          <div role="alert" className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="flex flex-wrap gap-4">
          <Input
            label={t("Search")}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            className="min-w-[260px]"
          />
          <Select
            label={t("Role")}
            options={roleOptions}
            value={roleFilter}
            onChange={(val: any) => setRoleFilter(val)}
            className="min-w-[200px]"
          />
          <Select
            label={t("Archived")}
            options={archivedOptions}
            value={showArchived ? "yes" : "no"}
            onChange={(val: any) => setShowArchived(val === "yes")}
            className="min-w-[220px]"
          />

          <div className="flex items-end gap-2">
            <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-foreground">
              {t("Total")}: {users.length}
            </span>
            <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-foreground">
              {t("Shown")}: {filteredUsers.length}
            </span>
          </div>
        </div>

        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Employee Id")}</TableHead>
                <TableHead>{t("Name")}</TableHead>
                <TableHead>{t("E-mail")}</TableHead>
                <TableHead>{t("Role")}</TableHead>
                <TableHead>{t("Department")}</TableHead>
                <TableHead>{t("Branch")}</TableHead>
                <TableHead>{t("Manager")}</TableHead>
                <TableHead>{t("Status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <div className="flex items-center gap-2 py-2">
                      <Spinner size="sm" />
                      <span className="text-sm text-muted-foreground">{t("Loading...")}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : null}

              {!loading && filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <p className="text-sm text-muted-foreground py-2">
                      {t("No users found")}
                    </p>
                  </TableCell>
                </TableRow>
              ) : null}

              {filteredUsers.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.employeeId}</TableCell>
                  <TableCell>{u.fullName}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.role}</TableCell>
                  <TableCell>{u.department?.name || ""}</TableCell>
                  <TableCell>{u.branch?.name || ""}</TableCell>
                  <TableCell>
                    {u.managerNames?.length ? u.managerNames.join(", ") : ""}
                  </TableCell>
                  <TableCell>
                    {u.isArchived ? (
                      <StatusBadge variant="neutral">{t("Archived")}</StatusBadge>
                    ) : (
                      <StatusBadge variant="success">{t("Active")}</StatusBadge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">
          {t("This page uses the unified users API")}: <code>FetchUsers('Company', companyId, archived)</code>
        </p>
      </div>
    </div>
  );
}
